// components/screener/screenerConfig.js
// Single source of truth for the screener UI.
// To add a new data source:
//   1. Add column definitions to COLUMNS
//   2. Add/update a view in VIEWS
//   3. Add a filter category in FILTER_CATEGORIES

// ─── Value formatting helpers ───────────────────────────────────────────────

export const shortNumber = (n) => {
    if (n === null || n === undefined) return '';
    const abs = Math.abs(n);
    if (abs >= 1e12) return (n / 1e12).toFixed(2) + 'T';
    if (abs >= 1e11) return (n / 1e9).toFixed(1) + 'B';   // 100B+ → 1 decimal
    if (abs >= 1e9) return (n / 1e9).toFixed(2) + 'B';     // 1B-100B → 2 decimals
    if (abs >= 1e6) return (n / 1e6).toFixed(2) + 'M';     // 1M+ → 2 decimals
    if (abs >= 1e3) return (n / 1e3).toFixed(1) + 'K';
    return n.toString();
};

export const formatPresetLabel = (value, format, direction) => {
    const formatted = format === 'currency' ? `$${shortNumber(value)}`
        : format === 'percentage' ? `${value}%`
            : format === 'shortNumber' ? shortNumber(value)
                : value.toString();

    if (direction === 'above') return `Above ${formatted}`;
    if (direction === 'below') return `Under ${formatted}`;
    return formatted;
};

export const formatBetweenLabel = (min, max, format) => {
    const fmt = (v) => format === 'currency' ? `$${shortNumber(v)}`
        : format === 'percentage' ? `${v}%`
            : format === 'shortNumber' ? shortNumber(v)
                : v.toString();
    return `${fmt(min)} – ${fmt(max)}`;
};

// ─── Column Definitions ─────────────────────────────────────────────────────
// Each column: { field, headerName, minWidth, flex, renderCell?, valueFormatter? }
// Add new columns here when adding data sources.

export const COLUMNS = {
    // Base / always available
    symbol: {
        field: 'symbol',
        headerName: 'Company',
        minWidth: 180,
        flex: 1.2,
        renderType: 'company', // Custom render handled by ResultsTable
    },
    price: {
        field: 'price',
        headerName: 'Price',
        minWidth: 100,
        flex: 0.8,
        renderType: 'currency',
    },
    changesPercentage: {
        field: 'changesPercentage',
        headerName: 'Chg %',
        minWidth: 90,
        flex: 0.7,
        renderType: 'changePercent',
    },
    change: {
        field: 'change',
        headerName: 'Chg',
        minWidth: 90,
        flex: 0.7,
        renderType: 'change',
    },
    marketCap: {
        field: 'marketCap',
        headerName: 'Mkt Cap',
        minWidth: 110,
        flex: 0.9,
        renderType: 'shortNumber',
    },
    volume: {
        field: 'volume',
        headerName: 'Volume',
        minWidth: 110,
        flex: 0.8,
        renderType: 'shortNumber',
    },
    exchange: {
        field: 'exchange',
        headerName: 'Exchange',
        minWidth: 100,
        flex: 0.7,
    },
    sector: {
        field: 'sector',
        headerName: 'Sector',
        minWidth: 130,
        flex: 1,
    },
    industry: {
        field: 'industry',
        headerName: 'Industry',
        minWidth: 150,
        flex: 1,
    },
    country: {
        field: 'country',
        headerName: 'Country',
        minWidth: 100,
        flex: 0.7,
    },

    // Price details
    open: { field: 'open', headerName: 'Open', minWidth: 90, flex: 0.7, renderType: 'number' },
    previousClose: { field: 'previousClose', headerName: 'Prev Close', minWidth: 100, flex: 0.8, renderType: 'number' },
    dayLow: { field: 'dayLow', headerName: 'Day Low', minWidth: 90, flex: 0.7, renderType: 'number' },
    dayHigh: { field: 'dayHigh', headerName: 'Day High', minWidth: 90, flex: 0.7, renderType: 'number' },
    yearLow: { field: 'yearLow', headerName: '52W Low', minWidth: 90, flex: 0.7, renderType: 'number' },
    yearHigh: { field: 'yearHigh', headerName: '52W High', minWidth: 90, flex: 0.7, renderType: 'number' },
    avgVolume: { field: 'avgVolume', headerName: 'Avg Vol', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },

    // Valuation
    pe: { field: 'pe', headerName: 'P/E', minWidth: 80, flex: 0.6, renderType: 'number' },
    eps: { field: 'eps', headerName: 'EPS', minWidth: 80, flex: 0.6, renderType: 'number' },
    dcf: { field: 'dcf', headerName: 'DCF', minWidth: 80, flex: 0.6, renderType: 'number' },
    dcfDiff: { field: 'dcfDiff', headerName: 'DCF Diff', minWidth: 90, flex: 0.7, renderType: 'number' },
    beta: { field: 'beta', headerName: 'Beta', minWidth: 80, flex: 0.6, renderType: 'number' },
    sharesOutstanding: { field: 'sharesOutstanding', headerName: 'Shares Out', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },
    earningsAnnouncement: {
        field: 'earningsAnnouncement',
        headerName: 'Earnings',
        minWidth: 110,
        flex: 0.8,
        renderType: 'date',
    },

    // Technical
    rsi: { field: 'rsi', headerName: 'RSI', minWidth: 70, flex: 0.5, renderType: 'number' },
    sma20: { field: 'sma20', headerName: 'SMA 20', minWidth: 90, flex: 0.7, renderType: 'number' },
    sma50: { field: 'sma50', headerName: 'SMA 50', minWidth: 90, flex: 0.7, renderType: 'number' },
    sma100: { field: 'sma100', headerName: 'SMA 100', minWidth: 100, flex: 0.7, renderType: 'number' },
    sma150: { field: 'sma150', headerName: 'SMA 150', minWidth: 100, flex: 0.7, renderType: 'number' },
    sma200: { field: 'sma200', headerName: 'SMA 200', minWidth: 100, flex: 0.7, renderType: 'number' },

    // Financial (Income Statement)
    revenue: { field: 'revenue', headerName: 'Revenue', minWidth: 110, flex: 0.9, renderType: 'shortNumber' },
    grossProfit: { field: 'grossProfit', headerName: 'Gross Profit', minWidth: 110, flex: 0.9, renderType: 'shortNumber' },
    operatingIncome: { field: 'operatingIncome', headerName: 'Op. Income', minWidth: 110, flex: 0.9, renderType: 'shortNumber' },
    netIncome: { field: 'netIncome', headerName: 'Net Income', minWidth: 110, flex: 0.9, renderType: 'shortNumber' },
    ebitda: { field: 'ebitda', headerName: 'EBITDA', minWidth: 110, flex: 0.9, renderType: 'shortNumber' },
    operatingExpenses: { field: 'operatingExpenses', headerName: 'Op. Expenses', minWidth: 120, flex: 0.9, renderType: 'shortNumber' },
    epsDiluted: { field: 'epsDiluted', headerName: 'EPS (Diluted)', minWidth: 110, flex: 0.8, renderType: 'number' },

    // Cash Flow
    freeCashFlow: { field: 'freeCashFlow', headerName: 'FCF', minWidth: 110, flex: 0.9, renderType: 'shortNumber' },
    operatingCashFlow: { field: 'operatingCashFlow', headerName: 'Op. Cash Flow', minWidth: 120, flex: 0.9, renderType: 'shortNumber' },
    capitalExpenditure: { field: 'capitalExpenditure', headerName: 'CapEx', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },
    commonDividendsPaid: { field: 'commonDividendsPaid', headerName: 'Div. Paid (neg)', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },
    commonStockRepurchased: { field: 'commonStockRepurchased', headerName: 'Buybacks', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },
    stockBasedCompensation: { field: 'stockBasedCompensation', headerName: 'SBC', minWidth: 90, flex: 0.7, renderType: 'shortNumber' },
    netChangeInCash: { field: 'netChangeInCash', headerName: 'Cash Change', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },
    cashAtEndOfPeriod: { field: 'cashAtEndOfPeriod', headerName: 'Cash Position', minWidth: 120, flex: 0.9, renderType: 'shortNumber' },
    netCashProvidedByOperatingActivities: { field: 'netCashProvidedByOperatingActivities', headerName: 'Cash from Ops', minWidth: 120, flex: 0.9, renderType: 'shortNumber' },
    netCashProvidedByInvestingActivities: { field: 'netCashProvidedByInvestingActivities', headerName: 'Cash from Inv', minWidth: 120, flex: 0.9, renderType: 'shortNumber' },
    netCashProvidedByFinancingActivities: { field: 'netCashProvidedByFinancingActivities', headerName: 'Cash from Fin', minWidth: 120, flex: 0.9, renderType: 'shortNumber' },

    // Analyst
    consensus: { field: 'consensus', headerName: 'Consensus', minWidth: 110, flex: 0.8, renderType: 'consensus' },
    strongBuy: { field: 'strongBuy', headerName: 'Strong Buy', minWidth: 90, flex: 0.6, renderType: 'number' },
    buy: { field: 'buy', headerName: 'Buy', minWidth: 70, flex: 0.5, renderType: 'number' },
    hold: { field: 'hold', headerName: 'Hold', minWidth: 70, flex: 0.5, renderType: 'number' },
    sell: { field: 'sell', headerName: 'Sell', minWidth: 70, flex: 0.5, renderType: 'number' },
    strongSell: { field: 'strongSell', headerName: 'Strong Sell', minWidth: 90, flex: 0.6, renderType: 'number' },

    // Rating
    rating: { field: 'rating', headerName: 'Grade', minWidth: 70, flex: 0.5 },
    ratingScore: { field: 'ratingScore', headerName: 'Score', minWidth: 70, flex: 0.5, renderType: 'number' },
    ratingRecommendation: { field: 'ratingRecommendation', headerName: 'Rating', minWidth: 110, flex: 0.8, renderType: 'consensus' },
    ratingDetailsDCFScore: { field: 'ratingDetailsDCFScore', headerName: 'DCF Score', minWidth: 90, flex: 0.5, renderType: 'number' },
    ratingDetailsROEScore: { field: 'ratingDetailsROEScore', headerName: 'ROE Score', minWidth: 90, flex: 0.5, renderType: 'number' },
    ratingDetailsROAScore: { field: 'ratingDetailsROAScore', headerName: 'ROA Score', minWidth: 90, flex: 0.5, renderType: 'number' },
    ratingDetailsDEScore: { field: 'ratingDetailsDEScore', headerName: 'D/E Score', minWidth: 90, flex: 0.5, renderType: 'number' },
    ratingDetailsPEScore: { field: 'ratingDetailsPEScore', headerName: 'P/E Score', minWidth: 90, flex: 0.5, renderType: 'number' },
    ratingDetailsPBScore: { field: 'ratingDetailsPBScore', headerName: 'P/B Score', minWidth: 90, flex: 0.5, renderType: 'number' },

    // Scores
    piotroskiScore: { field: 'piotroskiScore', headerName: 'Piotroski', minWidth: 90, flex: 0.6, renderType: 'number' },
    altmanZScore: { field: 'altmanZScore', headerName: 'Altman Z', minWidth: 90, flex: 0.6, renderType: 'number' },

    // Performance
    perf5D: { field: 'perf5D', headerName: '5D %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perf1M: { field: 'perf1M', headerName: '1M %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perf3M: { field: 'perf3M', headerName: '3M %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perf6M: { field: 'perf6M', headerName: '6M %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perfYtd: { field: 'perfYtd', headerName: 'YTD %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perf1Y: { field: 'perf1Y', headerName: '1Y %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perf3Y: { field: 'perf3Y', headerName: '3Y %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perf5Y: { field: 'perf5Y', headerName: '5Y %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perf10Y: { field: 'perf10Y', headerName: '10Y %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },
    perfMax: { field: 'perfMax', headerName: 'Max %', minWidth: 80, flex: 0.6, renderType: 'changePercent' },

    // Key Metrics TTM
    enterpriseValueTTM: { field: 'enterpriseValueTTM', headerName: 'EV', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },
    evToEBITDATTM: { field: 'evToEBITDATTM', headerName: 'EV/EBITDA', minWidth: 100, flex: 0.7, renderType: 'number' },
    evToSalesTTM: { field: 'evToSalesTTM', headerName: 'EV/Sales', minWidth: 90, flex: 0.7, renderType: 'number' },
    evToFreeCashFlowTTM: { field: 'evToFreeCashFlowTTM', headerName: 'EV/FCF', minWidth: 90, flex: 0.7, renderType: 'number' },
    evToOperatingCashFlowTTM: { field: 'evToOperatingCashFlowTTM', headerName: 'EV/OCF', minWidth: 90, flex: 0.7, renderType: 'number' },
    returnOnEquityTTM: { field: 'returnOnEquityTTM', headerName: 'ROE', minWidth: 80, flex: 0.6, renderType: 'percent' },
    returnOnAssetsTTM: { field: 'returnOnAssetsTTM', headerName: 'ROA', minWidth: 80, flex: 0.6, renderType: 'percent' },
    returnOnInvestedCapitalTTM: { field: 'returnOnInvestedCapitalTTM', headerName: 'ROIC', minWidth: 80, flex: 0.6, renderType: 'percent' },
    returnOnCapitalEmployedTTM: { field: 'returnOnCapitalEmployedTTM', headerName: 'ROCE', minWidth: 80, flex: 0.6, renderType: 'percent' },
    currentRatioTTM: { field: 'currentRatioTTM', headerName: 'Current Ratio', minWidth: 110, flex: 0.7, renderType: 'number' },
    netDebtToEBITDATTM: { field: 'netDebtToEBITDATTM', headerName: 'Net Debt/EBITDA', minWidth: 130, flex: 0.8, renderType: 'number' },
    earningsYieldTTM: { field: 'earningsYieldTTM', headerName: 'Earn. Yield', minWidth: 100, flex: 0.7, renderType: 'percent' },
    freeCashFlowYieldTTM: { field: 'freeCashFlowYieldTTM', headerName: 'FCF Yield', minWidth: 100, flex: 0.7, renderType: 'percent' },
    grahamNumberTTM: { field: 'grahamNumberTTM', headerName: 'Graham #', minWidth: 100, flex: 0.7, renderType: 'number' },
    workingCapitalTTM: { field: 'workingCapitalTTM', headerName: 'Work. Capital', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },
    tangibleAssetValueTTM: { field: 'tangibleAssetValueTTM', headerName: 'Tang. Assets', minWidth: 110, flex: 0.8, renderType: 'shortNumber' },

    // Financial Ratios TTM — Margins
    grossProfitMarginTTM: { field: 'grossProfitMarginTTM', headerName: 'Gross Margin', minWidth: 110, flex: 0.7, renderType: 'percent' },
    operatingProfitMarginTTM: { field: 'operatingProfitMarginTTM', headerName: 'Op. Margin', minWidth: 100, flex: 0.7, renderType: 'percent' },
    netProfitMarginTTM: { field: 'netProfitMarginTTM', headerName: 'Net Margin', minWidth: 100, flex: 0.7, renderType: 'percent' },
    ebitdaMarginTTM: { field: 'ebitdaMarginTTM', headerName: 'EBITDA Margin', minWidth: 120, flex: 0.7, renderType: 'percent' },
    ebitMarginTTM: { field: 'ebitMarginTTM', headerName: 'EBIT Margin', minWidth: 110, flex: 0.7, renderType: 'percent' },
    pretaxProfitMarginTTM: { field: 'pretaxProfitMarginTTM', headerName: 'Pretax Margin', minWidth: 120, flex: 0.7, renderType: 'percent' },

    // Financial Ratios TTM — Price Ratios
    priceToBookRatioTTM: { field: 'priceToBookRatioTTM', headerName: 'P/B', minWidth: 70, flex: 0.5, renderType: 'number' },
    priceToSalesRatioTTM: { field: 'priceToSalesRatioTTM', headerName: 'P/S', minWidth: 70, flex: 0.5, renderType: 'number' },
    priceToEarningsGrowthRatioTTM: { field: 'priceToEarningsGrowthRatioTTM', headerName: 'PEG', minWidth: 70, flex: 0.5, renderType: 'number' },
    priceToFreeCashFlowRatioTTM: { field: 'priceToFreeCashFlowRatioTTM', headerName: 'P/FCF', minWidth: 80, flex: 0.6, renderType: 'number' },
    priceToOperatingCashFlowRatioTTM: { field: 'priceToOperatingCashFlowRatioTTM', headerName: 'P/OCF', minWidth: 80, flex: 0.6, renderType: 'number' },
    priceToFairValueTTM: { field: 'priceToFairValueTTM', headerName: 'Price/FV', minWidth: 70, flex: 0.5, renderType: 'number' },

    // Financial Ratios TTM — Leverage
    debtToEquityRatioTTM: { field: 'debtToEquityRatioTTM', headerName: 'D/E', minWidth: 70, flex: 0.5, renderType: 'number' },
    debtToAssetsRatioTTM: { field: 'debtToAssetsRatioTTM', headerName: 'D/A', minWidth: 70, flex: 0.5, renderType: 'number' },

    // Financial Ratios TTM — Liquidity & Coverage
    quickRatioTTM: { field: 'quickRatioTTM', headerName: 'Quick Ratio', minWidth: 100, flex: 0.7, renderType: 'number' },
    interestCoverageRatioTTM: { field: 'interestCoverageRatioTTM', headerName: 'Int. Coverage', minWidth: 110, flex: 0.7, renderType: 'number' },

    // Financial Ratios TTM — Dividends
    dividendYieldTTM: { field: 'dividendYieldTTM', headerName: 'Div. Yield', minWidth: 90, flex: 0.6, renderType: 'percent' },
    dividendPayoutRatioTTM: { field: 'dividendPayoutRatioTTM', headerName: 'Payout Ratio', minWidth: 110, flex: 0.7, renderType: 'percent' },
    dividendPerShareTTM: { field: 'dividendPerShareTTM', headerName: 'Div/Share', minWidth: 90, flex: 0.6, renderType: 'number' },

    // Financial Ratios TTM — Per Share
    bookValuePerShareTTM: { field: 'bookValuePerShareTTM', headerName: 'Book/Share', minWidth: 100, flex: 0.7, renderType: 'number' },
    cashPerShareTTM: { field: 'cashPerShareTTM', headerName: 'Cash/Share', minWidth: 100, flex: 0.7, renderType: 'number' },
    revenuePerShareTTM: { field: 'revenuePerShareTTM', headerName: 'Rev/Share', minWidth: 100, flex: 0.7, renderType: 'number' },
    freeCashFlowPerShareTTM: { field: 'freeCashFlowPerShareTTM', headerName: 'FCF/Share', minWidth: 100, flex: 0.7, renderType: 'number' },

    // Financial Ratios TTM — Tax & Turnover
    effectiveTaxRateTTM: { field: 'effectiveTaxRateTTM', headerName: 'Tax Rate', minWidth: 90, flex: 0.6, renderType: 'percent' },
    assetTurnoverTTM: { field: 'assetTurnoverTTM', headerName: 'Asset Turnover', minWidth: 100, flex: 0.7, renderType: 'number' },
};

// ─── View/Tab Definitions ───────────────────────────────────────────────────
// Each view maps to a backend view name and defines which columns to show.
// Add new views or columns easily here.

export const VIEWS = [
    {
        id: 'overview',
        label: 'Overview',
        columns: ['symbol', 'price', 'changesPercentage', 'change', 'marketCap', 'volume', 'exchange', 'sector'],
    },
    {
        id: 'valuation',
        label: 'Valuation',
        columns: [
            'symbol', 'price', 'marketCap', 'pe', 'eps',
            'priceToBookRatioTTM', 'priceToSalesRatioTTM', 'priceToEarningsGrowthRatioTTM',
            'priceToFreeCashFlowRatioTTM', 'priceToFairValueTTM',
            'evToEBITDATTM', 'evToSalesTTM', 'evToFreeCashFlowTTM', 'enterpriseValueTTM',
            'earningsYieldTTM', 'freeCashFlowYieldTTM',
            'returnOnEquityTTM', 'returnOnAssetsTTM', 'returnOnInvestedCapitalTTM',
            'currentRatioTTM', 'quickRatioTTM', 'debtToEquityRatioTTM',
            'grossProfitMarginTTM', 'operatingProfitMarginTTM', 'netProfitMarginTTM',
            'dividendYieldTTM', 'dividendPayoutRatioTTM',
            'piotroskiScore', 'altmanZScore',
        ],
    },
    {
        id: 'financial',
        label: 'Financial',
        columns: ['symbol', 'price', 'marketCap', 'revenue', 'grossProfit', 'operatingIncome', 'netIncome', 'ebitda', 'epsDiluted', 'freeCashFlow', 'operatingCashFlow', 'capitalExpenditure', 'cashAtEndOfPeriod', 'netChangeInCash', 'commonDividendsPaid', 'commonStockRepurchased', 'stockBasedCompensation'],
    },
    {
        id: 'technical',
        label: 'Technical',
        columns: ['symbol', 'price', 'changesPercentage', 'rsi', 'sma20', 'sma50', 'sma100', 'sma150', 'sma200'],
    },
    {
        id: 'performance',
        label: 'Performance',
        columns: ['symbol', 'price', 'changesPercentage', 'perf5D', 'perf1M', 'perf3M', 'perf6M', 'perfYtd', 'perf1Y', 'perf3Y', 'perf5Y', 'perf10Y', 'perfMax'],
    },
    {
        id: 'analyst',
        label: 'Analyst',
        columns: ['symbol', 'price', 'marketCap', 'consensus', 'strongBuy', 'buy', 'hold', 'sell', 'strongSell', 'rating', 'ratingScore', 'ratingRecommendation'],
    },
    {
        id: 'all',
        label: 'Custom',
        columns: Object.keys(COLUMNS),
    },
];

// ─── Filter Categories ──────────────────────────────────────────────────────
// Defines what filters appear in the "Add Filter" dialog, their types, and presets.
// `optionsKey` means the filter needs runtime data (from hooks like useExchanges).
// `format` controls how preset labels are displayed.

export const FILTER_CATEGORIES = [
    {
        id: 'priceVolume',
        label: 'Price & Volume',
        filters: [
            {
                id: 'price', label: 'Price', type: 'range', format: 'currency',
                presets: {
                    above: [1, 5, 10, 20, 50, 100, 200, 500, 1000],
                    below: [1, 5, 10, 20, 50, 100, 200, 500, 1000],
                    between: [[1, 5], [5, 10], [10, 20], [20, 50], [50, 100], [100, 200], [200, 500]],
                },
            },
            {
                id: 'marketCap', label: 'Market Cap', type: 'range', format: 'shortNumber',
                presets: {
                    above: [1e6, 1e7, 1e8, 5e8, 1e9, 1e10, 5e10, 1e11, 5e11, 1e12],
                    below: [1e6, 1e7, 1e8, 5e8, 1e9, 1e10, 5e10, 1e11, 5e11, 1e12],
                    between: [[1e6, 1e7], [1e7, 1e8], [1e8, 5e8], [5e8, 1e9], [1e9, 1e10], [1e10, 5e10], [5e10, 1e11]],
                },
            },
            {
                id: 'changesPercentage', label: 'Change %', type: 'range', format: 'percentage',
                presets: {
                    above: [1, 5, 10, 20, 50],
                    below: [-1, -5, -10, -20, -50],
                    between: [[-5, 5], [-10, 10], [0, 10], [0, 20]],
                },
            },
            {
                id: 'change', label: 'Change Amount', type: 'range', format: 'currency',
                presets: {
                    above: [1, 5, 10, 20, 50, 100],
                    below: [1, 5, 10, 20, 50, 100],
                    between: [[1, 5], [5, 10], [10, 20], [20, 50]],
                },
            },
            {
                id: 'volume', label: 'Volume', type: 'range', format: 'shortNumber',
                presets: {
                    above: [1e5, 5e5, 1e6, 5e6, 1e7, 5e7, 1e8],
                    below: [1e5, 5e5, 1e6, 5e6, 1e7, 5e7, 1e8],
                    between: [[1e5, 1e6], [1e6, 1e7], [1e7, 1e8]],
                },
            },
            {
                id: 'avgVolume', label: 'Avg Volume', type: 'range', format: 'shortNumber',
                presets: {
                    above: [1e5, 5e5, 1e6, 5e6, 1e7, 5e7, 1e8],
                    below: [1e5, 5e5, 1e6, 5e6, 1e7, 5e7, 1e8],
                    between: [[1e5, 1e6], [1e6, 1e7], [1e7, 1e8]],
                },
            },
            { id: 'open', label: 'Open', type: 'range', format: 'currency', presets: { above: [1, 5, 10, 20, 50, 100, 200, 500], below: [1, 5, 10, 20, 50, 100, 200, 500], between: [[1, 5], [5, 10], [10, 50], [50, 100]] } },
            { id: 'previousClose', label: 'Prev Close', type: 'range', format: 'currency', presets: { above: [1, 5, 10, 20, 50, 100, 200, 500], below: [1, 5, 10, 20, 50, 100, 200, 500], between: [[1, 5], [5, 10], [10, 50], [50, 100]] } },
            { id: 'dayLow', label: 'Day Low', type: 'range', format: 'currency', presets: { above: [1, 5, 10, 20, 50, 100], below: [1, 5, 10, 20, 50, 100], between: [[1, 5], [5, 10], [10, 50]] } },
            { id: 'dayHigh', label: 'Day High', type: 'range', format: 'currency', presets: { above: [1, 5, 10, 20, 50, 100], below: [1, 5, 10, 20, 50, 100], between: [[1, 5], [5, 10], [10, 50]] } },
            { id: 'yearLow', label: '52W Low', type: 'range', format: 'currency', presets: { above: [1, 5, 10, 20, 50, 100, 200], below: [1, 5, 10, 20, 50, 100, 200], between: [[1, 10], [10, 50], [50, 100]] } },
            { id: 'yearHigh', label: '52W High', type: 'range', format: 'currency', presets: { above: [1, 5, 10, 20, 50, 100, 200, 500], below: [1, 5, 10, 20, 50, 100, 200, 500], between: [[1, 10], [10, 50], [50, 200]] } },
        ],
    },
    {
        id: 'fundamentals',
        label: 'Fundamentals',
        filters: [
            { id: 'pe', label: 'P/E Ratio', type: 'range', format: 'number', presets: { above: [5, 10, 15, 20, 30, 50, 100], below: [5, 10, 15, 20, 30, 50, 100], between: [[0, 10], [10, 20], [20, 50]] } },
            { id: 'eps', label: 'EPS', type: 'range', format: 'number', presets: { above: [0, 1, 2, 5, 10], below: [0, 1, 2, 5, 10], between: [[0, 1], [1, 5], [5, 10]] } },
            { id: 'beta', label: 'Beta', type: 'range', format: 'number', presets: { above: [0.5, 1, 1.5, 2, 3], below: [0.5, 1, 1.5, 2, 3], between: [[0, 1], [1, 2], [2, 3]] } },
            { id: 'dcf', label: 'DCF', type: 'range', format: 'currency', presets: { above: [10, 20, 50, 100, 200], below: [10, 20, 50, 100, 200], between: [[10, 50], [50, 100], [100, 200]] } },
            { id: 'dcfDiff', label: 'DCF Diff', type: 'range', format: 'percentage', presets: { above: [10, 20, 30, 50, 100], below: [-10, -20, -30, -50, -100], between: [[-20, 20], [-50, 50], [0, 50]] } },
            { id: 'sharesOutstanding', label: 'Shares Out', type: 'range', format: 'shortNumber', presets: { above: [1e6, 1e7, 1e8, 5e8, 1e9, 5e9, 1e10], below: [1e6, 1e7, 1e8, 5e8, 1e9, 5e9, 1e10], between: [[1e6, 1e7], [1e7, 1e8], [1e8, 1e9], [1e9, 1e10]] } },
            {
                id: 'rsi', label: 'RSI', type: 'range', format: 'number',
                presets: {
                    above: [30, 50, 70, 80, 90],
                    below: [10, 20, 30, 50, 70],
                    between: [[0, 30], [30, 70], [70, 100], [20, 80], [40, 60]],
                },
            },
            { id: 'averageComparison', label: 'Moving Average', type: 'composite' },
        ],
    },
    {
        id: 'company',
        label: 'Company',
        filters: [
            { id: 'exchange', label: 'Exchange', type: 'multiSelect', optionsKey: 'exchanges' },
            { id: 'sector', label: 'Sector', type: 'select', optionsKey: 'sectors' },
            { id: 'industry', label: 'Industry', type: 'select', optionsKey: 'industries' },
            { id: 'country', label: 'Country', type: 'select', optionsKey: 'countries' },
            { id: 'isEtf', label: 'ETF Only', type: 'boolean' },
        ],
    },
    {
        id: 'financial',
        label: 'Financial',
        filters: [
            { id: 'revenue', label: 'Revenue', type: 'range', format: 'shortNumber', presets: { above: [1e6, 1e7, 1e8, 5e8, 1e9, 1e10, 5e10, 1e11], below: [1e6, 1e7, 1e8, 5e8, 1e9, 1e10], between: [[1e6, 1e8], [1e8, 1e9], [1e9, 1e10]] } },
            { id: 'netIncome', label: 'Net Income', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 1e9, 1e10], below: [0, 1e6, 1e7, 1e8], between: [[0, 1e8], [1e8, 1e9], [1e9, 1e10]] } },
            { id: 'grossProfit', label: 'Gross Profit', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 1e9, 1e10], below: [1e6, 1e7, 1e8, 1e9], between: [[0, 1e8], [1e8, 1e9]] } },
            { id: 'operatingIncome', label: 'Op. Income', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 1e9], below: [0, 1e6, 1e7, 1e8], between: [[0, 1e8], [1e8, 1e9]] } },
            { id: 'ebitda', label: 'EBITDA', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 1e9, 1e10], below: [0, 1e6, 1e7, 1e8], between: [[0, 1e8], [1e8, 1e9]] } },
            { id: 'epsDiluted', label: 'EPS (Diluted)', type: 'range', format: 'number', presets: { above: [0, 1, 2, 5, 10, 20], below: [0, 1, 2, 5, 10], between: [[0, 2], [2, 5], [5, 10]] } },
        ],
    },
    {
        id: 'cashFlow',
        label: 'Cash Flow',
        filters: [
            { id: 'freeCashFlow', label: 'Free Cash Flow', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 5e8, 1e9, 5e9, 1e10], below: [0, 1e6, 1e7, 1e8, 5e8, 1e9], between: [[0, 1e8], [1e8, 1e9], [1e9, 1e10]] } },
            { id: 'operatingCashFlow', label: 'Operating Cash Flow', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 5e8, 1e9, 5e9, 1e10], below: [0, 1e6, 1e7, 1e8, 5e8, 1e9], between: [[0, 1e8], [1e8, 1e9], [1e9, 1e10]] } },
            { id: 'capitalExpenditure', label: 'Capital Expenditure', type: 'range', format: 'shortNumber', presets: { above: [1e6, 1e7, 1e8, 5e8, 1e9, 5e9], below: [1e6, 1e7, 1e8, 5e8, 1e9], between: [[1e6, 1e8], [1e8, 1e9], [1e9, 1e10]] } },
            { id: 'commonDividendsPaid', label: 'Dividends Paid(neg.)', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 5e8, 1e9], below: [0, -1e6, -1e7, -1e8, -5e8, -1e9] } },
            { id: 'commonStockRepurchased', label: 'Stock Buybacks (neg.)', type: 'range', format: 'shortNumber', presets: { above: [0], below: [-1e6, -1e7, -1e8, -5e8, -1e9, -5e9, -1e10] } },
            { id: 'stockBasedCompensation', label: 'Stock-Based Comp', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 5e8, 1e9], below: [1e6, 1e7, 1e8, 5e8, 1e9], between: [[0, 1e7], [1e7, 1e8], [1e8, 1e9]] } },
            { id: 'netChangeInCash', label: 'Net Change in Cash', type: 'range', format: 'shortNumber', presets: { above: [0, 1e6, 1e7, 1e8, 5e8, 1e9], below: [0, -1e6, -1e7, -1e8, -5e8, -1e9] } },
        ],
    },
    {
        id: 'analyst',
        label: 'Analyst',
        filters: [
            { id: 'consensus', label: 'Consensus', type: 'multiSelect', options: ['Strong Buy', 'Buy', 'Hold', 'Sell', 'Strong Sell'] },
            { id: 'strongBuy', label: 'Strong Buy #', type: 'range', format: 'number', presets: { above: [1, 3, 5, 10, 15], below: [1, 3, 5, 10], between: [[1, 5], [5, 10], [10, 20]] } },
            { id: 'buy', label: 'Buy #', type: 'range', format: 'number', presets: { above: [1, 3, 5, 10, 15], below: [1, 3, 5, 10], between: [[1, 5], [5, 10]] } },
            { id: 'hold', label: 'Hold #', type: 'range', format: 'number', presets: { above: [1, 3, 5, 10], below: [1, 3, 5, 10], between: [[1, 5], [5, 10]] } },
            { id: 'sell', label: 'Sell #', type: 'range', format: 'number', presets: { above: [1, 3, 5, 10], below: [1, 3, 5, 10], between: [[1, 5], [5, 10]] } },
            { id: 'strongSell', label: 'Strong Sell #', type: 'range', format: 'number', presets: { above: [1, 3, 5, 10], below: [1, 3, 5, 10], between: [[1, 5], [5, 10]] } },
        ],
    },
    {
        id: 'rating',
        label: 'Rating',
        filters: [
            { id: 'ratingRecommendation', label: 'Recommendation', type: 'multiSelect', options: ['Strong Buy', 'Buy', 'Neutral', 'Sell', 'Strong Sell'] },
            { id: 'rating', label: 'Grade', type: 'multiSelect', options: ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D+', 'D', 'D-', 'F'] },
            { id: 'ratingScore', label: 'Overall Score', type: 'range', format: 'number', presets: { above: [1, 2, 3, 4], below: [2, 3, 4, 5], between: [[1, 2], [2, 3], [3, 4], [4, 5]] } },
            { id: 'ratingDetailsDCFScore', label: 'DCF Score', type: 'range', format: 'number', presets: { above: [1, 2, 3, 4], below: [2, 3, 4, 5] } },
            { id: 'ratingDetailsROEScore', label: 'ROE Score', type: 'range', format: 'number', presets: { above: [1, 2, 3, 4], below: [2, 3, 4, 5] } },
            { id: 'ratingDetailsROAScore', label: 'ROA Score', type: 'range', format: 'number', presets: { above: [1, 2, 3, 4], below: [2, 3, 4, 5] } },
            { id: 'ratingDetailsDEScore', label: 'D/E Score', type: 'range', format: 'number', presets: { above: [1, 2, 3, 4], below: [2, 3, 4, 5] } },
            { id: 'ratingDetailsPEScore', label: 'P/E Score', type: 'range', format: 'number', presets: { above: [1, 2, 3, 4], below: [2, 3, 4, 5] } },
            { id: 'ratingDetailsPBScore', label: 'P/B Score', type: 'range', format: 'number', presets: { above: [1, 2, 3, 4], below: [2, 3, 4, 5] } },
        ],
    },
    {
        id: 'scores',
        label: 'Scores',
        filters: [
            { id: 'piotroskiScore', label: 'Piotroski Score', type: 'range', format: 'number', presets: { above: [1, 3, 5, 7, 8], below: [2, 4, 6, 8, 9], between: [[0, 3], [3, 6], [6, 9], [7, 9]] } },
            { id: 'altmanZScore', label: 'Altman Z-Score', type: 'range', format: 'number', presets: { above: [1.8, 2.5, 3, 5, 10], below: [1.8, 2.5, 3, 5], between: [[0, 1.8], [1.8, 3], [3, 10]] } },
        ],
    },
    {
        id: 'keyMetrics',
        label: 'Key Metrics',
        filters: [
            { id: 'evToEBITDATTM', label: 'EV/EBITDA', type: 'range', format: 'number', presets: { above: [5, 10, 15, 20, 30, 50], below: [5, 10, 15, 20, 30, 50], between: [[0, 10], [10, 20], [20, 50]] } },
            { id: 'evToSalesTTM', label: 'EV/Sales', type: 'range', format: 'number', presets: { above: [1, 2, 5, 10, 20], below: [1, 2, 5, 10, 20], between: [[0, 2], [2, 5], [5, 10]] } },
            { id: 'evToFreeCashFlowTTM', label: 'EV/FCF', type: 'range', format: 'number', presets: { above: [5, 10, 15, 20, 30, 50], below: [5, 10, 15, 20, 30], between: [[0, 15], [15, 30], [30, 50]] } },
            { id: 'evToOperatingCashFlowTTM', label: 'EV/OCF', type: 'range', format: 'number', presets: { above: [5, 10, 15, 20, 30], below: [5, 10, 15, 20, 30], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'enterpriseValueTTM', label: 'Enterprise Value', type: 'range', format: 'shortNumber', presets: { above: [1e8, 1e9, 1e10, 5e10, 1e11, 5e11], below: [1e8, 1e9, 1e10, 5e10, 1e11] } },
            { id: 'returnOnEquityTTM', label: 'ROE %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 5, 10, 15, 20, 30], below: [0, 5, 10, 15, 20], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'returnOnAssetsTTM', label: 'ROA %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 2, 5, 10, 15, 20], below: [0, 2, 5, 10], between: [[0, 5], [5, 10], [10, 20]] } },
            { id: 'returnOnInvestedCapitalTTM', label: 'ROIC %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 5, 10, 15, 20, 30], below: [0, 5, 10, 15], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'returnOnCapitalEmployedTTM', label: 'ROCE %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 5, 10, 15, 20, 30], below: [0, 5, 10, 15], between: [[0, 10], [10, 20]] } },
            { id: 'currentRatioTTM', label: 'Current Ratio', type: 'range', format: 'number', presets: { above: [0.5, 1, 1.5, 2, 3], below: [0.5, 1, 1.5, 2, 3], between: [[0, 1], [1, 2], [2, 3]] } },
            { id: 'netDebtToEBITDATTM', label: 'Net Debt/EBITDA', type: 'range', format: 'number', presets: { above: [0, 1, 2, 3, 5], below: [0, 1, 2, 3, 5], between: [[0, 2], [2, 4], [4, 6]] } },
            { id: 'earningsYieldTTM', label: 'Earnings Yield %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 2, 5, 8, 10, 15], below: [0, 2, 5, 8], between: [[0, 5], [5, 10], [10, 20]] } },
            { id: 'freeCashFlowYieldTTM', label: 'FCF Yield %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 2, 5, 8, 10, 15], below: [0, 2, 5, 8], between: [[0, 5], [5, 10], [10, 20]] } },
            { id: 'grahamNumberTTM', label: 'Graham Number', type: 'range', format: 'number', presets: { above: [5, 10, 20, 50, 100, 200], below: [5, 10, 20, 50, 100, 200] } },
        ],
    },
    {
        id: 'ratiosMargins',
        label: 'Ratios & Margins',
        filters: [
            { id: 'grossProfitMarginTTM', label: 'Gross Margin %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 10, 20, 30, 40, 50, 60, 70], below: [10, 20, 30, 40, 50, 60], between: [[0, 20], [20, 40], [40, 60], [60, 80]] } },
            { id: 'operatingProfitMarginTTM', label: 'Operating Margin %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 5, 10, 15, 20, 30], below: [0, 5, 10, 15, 20], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'netProfitMarginTTM', label: 'Net Margin %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 5, 10, 15, 20, 30], below: [0, 5, 10, 15, 20], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'ebitdaMarginTTM', label: 'EBITDA Margin %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 10, 15, 20, 30, 40], below: [0, 10, 15, 20, 30], between: [[0, 15], [15, 30], [30, 50]] } },
            { id: 'ebitMarginTTM', label: 'EBIT Margin %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 5, 10, 15, 20, 30], below: [0, 5, 10, 15, 20], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'pretaxProfitMarginTTM', label: 'Pretax Margin %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 5, 10, 15, 20, 30], below: [0, 5, 10, 15, 20], between: [[0, 10], [10, 20]] } },
            { id: 'priceToBookRatioTTM', label: 'P/B Ratio', type: 'range', format: 'number', presets: { above: [0.5, 1, 2, 3, 5, 10], below: [0.5, 1, 2, 3, 5, 10], between: [[0, 1], [1, 3], [3, 5], [5, 10]] } },
            { id: 'priceToSalesRatioTTM', label: 'P/S Ratio', type: 'range', format: 'number', presets: { above: [0.5, 1, 2, 5, 10, 20], below: [0.5, 1, 2, 5, 10], between: [[0, 1], [1, 3], [3, 5], [5, 10]] } },
            { id: 'priceToEarningsGrowthRatioTTM', label: 'PEG Ratio', type: 'range', format: 'number', presets: { above: [0.5, 1, 1.5, 2, 3], below: [0.5, 1, 1.5, 2, 3], between: [[0, 1], [1, 2], [2, 3]] } },
            { id: 'priceToFreeCashFlowRatioTTM', label: 'Price/FCF', type: 'range', format: 'number', presets: { above: [5, 10, 15, 20, 30, 50], below: [5, 10, 15, 20, 30], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'priceToOperatingCashFlowRatioTTM', label: 'Price/OCF', type: 'range', format: 'number', presets: { above: [5, 10, 15, 20, 30], below: [5, 10, 15, 20, 30], between: [[0, 10], [10, 20], [20, 40]] } },
            { id: 'priceToFairValueTTM', label: 'Price/Fair Value', type: 'range', format: 'number', presets: { above: [0.5, 0.8, 1, 1.2, 1.5, 2], below: [0.5, 0.8, 1, 1.2, 1.5], between: [[0, 0.8], [0.8, 1.2], [1.2, 2]] } },
            { id: 'debtToEquityRatioTTM', label: 'Debt/Equity', type: 'range', format: 'number', presets: { above: [0, 0.5, 1, 1.5, 2, 3], below: [0.5, 1, 1.5, 2, 3], between: [[0, 0.5], [0.5, 1], [1, 2]] } },
            { id: 'debtToAssetsRatioTTM', label: 'Debt/Assets', type: 'range', format: 'number', presets: { above: [0, 0.1, 0.2, 0.3, 0.5], below: [0.1, 0.2, 0.3, 0.5, 0.7], between: [[0, 0.2], [0.2, 0.4], [0.4, 0.6]] } },
            { id: 'quickRatioTTM', label: 'Quick Ratio', type: 'range', format: 'number', presets: { above: [0.5, 1, 1.5, 2, 3], below: [0.5, 1, 1.5, 2, 3], between: [[0, 1], [1, 2], [2, 3]] } },
            { id: 'interestCoverageRatioTTM', label: 'Interest Coverage', type: 'range', format: 'number', presets: { above: [1, 2, 3, 5, 10, 20], below: [1, 2, 3, 5, 10], between: [[0, 3], [3, 10], [10, 20]] } },
            { id: 'dividendYieldTTM', label: 'Dividend Yield %', type: 'range', format: 'number', filterScale: 0.01, presets: { above: [0, 1, 2, 3, 4, 5, 7, 10], below: [1, 2, 3, 5, 7, 10], between: [[0, 2], [2, 4], [4, 7], [7, 10]] } },
            { id: 'dividendPayoutRatioTTM', label: 'Payout Ratio', type: 'range', format: 'number', presets: { above: [0, 0.2, 0.3, 0.5, 0.7, 1], below: [0.3, 0.5, 0.7, 1], between: [[0, 0.3], [0.3, 0.6], [0.6, 1]] } },
            { id: 'effectiveTaxRateTTM', label: 'Effective Tax Rate', type: 'range', format: 'number', presets: { above: [0, 0.1, 0.15, 0.2, 0.25, 0.3], below: [0.1, 0.15, 0.2, 0.25, 0.3], between: [[0, 0.15], [0.15, 0.25], [0.25, 0.35]] } },
        ],
    },
    {
        id: 'performance',
        label: 'Performance',
        filters: [
            { id: 'perf5D', label: '5 Day %', type: 'range', format: 'percentage', presets: { above: [0, 2, 5, 10, 20], below: [0, -2, -5, -10, -20], between: [[-5, 5], [-10, 10], [0, 10]] } },
            { id: 'perf1M', label: '1 Month %', type: 'range', format: 'percentage', presets: { above: [0, 5, 10, 20, 50, 100], below: [0, -5, -10, -20, -50, -75], between: [[-10, 10], [-20, 20], [0, 20], [0, 50]] } },
            { id: 'perf3M', label: '3 Month %', type: 'range', format: 'percentage', presets: { above: [0, 5, 10, 20, 50, 100], below: [0, -5, -10, -20, -50, -75], between: [[-10, 10], [-20, 20], [0, 20], [0, 50]] } },
            { id: 'perf6M', label: '6 Month %', type: 'range', format: 'percentage', presets: { above: [0, 5, 10, 20, 50, 100], below: [0, -5, -10, -20, -50, -75], between: [[-10, 10], [-20, 20], [0, 20], [0, 50]] } },
            { id: 'perfYtd', label: 'YTD %', type: 'range', format: 'percentage', presets: { above: [0, 5, 10, 20, 50, 100], below: [0, -5, -10, -20, -50, -75], between: [[-10, 10], [-20, 20], [0, 20], [0, 50]] } },
            { id: 'perf1Y', label: '1 Year %', type: 'range', format: 'percentage', presets: { above: [0, 10, 20, 50, 100, 200], below: [0, -10, -20, -50, -75], between: [[-20, 20], [0, 50], [0, 100]] } },
            { id: 'perf3Y', label: '3 Year %', type: 'range', format: 'percentage', presets: { above: [0, 20, 50, 100, 200, 500], below: [0, -20, -50, -75], between: [[-50, 50], [0, 100], [0, 200]] } },
            { id: 'perf5Y', label: '5 Year %', type: 'range', format: 'percentage', presets: { above: [0, 50, 100, 200, 500, 1000], below: [0, -20, -50, -75], between: [[0, 100], [0, 500]] } },
            { id: 'perf10Y', label: '10 Year %', type: 'range', format: 'percentage', presets: { above: [0, 50, 100, 200, 500, 1000], below: [0, -20, -50, -75], between: [[0, 100], [0, 500]] } },
            { id: 'perfMax', label: 'Max %', type: 'range', format: 'percentage', presets: { above: [0, 50, 100, 200, 500, 1000], below: [0, -20, -50, -75], between: [[0, 100], [0, 500]] } },
        ],
    },
];

// ─── Helper: Get all filters flat ───────────────────────────────────────────
export const getAllFilters = () => FILTER_CATEGORIES.flatMap(cat => cat.filters);

// ─── Helper: Find a filter definition by ID ─────────────────────────────────
export const getFilterById = (id) => getAllFilters().find(f => f.id === id);

// ─── Column groups for the Custom tab picker ────────────────────────────────
export const COLUMN_GROUPS = [
    { label: 'Price & Volume', columns: ['price', 'changesPercentage', 'change', 'marketCap', 'volume', 'avgVolume', 'open', 'previousClose', 'dayLow', 'dayHigh', 'yearLow', 'yearHigh'] },
    { label: 'Company', columns: ['exchange', 'sector', 'industry', 'country'] },
    { label: 'Valuation', columns: ['pe', 'eps', 'dcf', 'dcfDiff', 'beta', 'sharesOutstanding', 'earningsAnnouncement'] },
    { label: 'Technical', columns: ['rsi', 'sma20', 'sma50', 'sma100', 'sma150', 'sma200'] },
    { label: 'Financial', columns: ['revenue', 'grossProfit', 'operatingIncome', 'netIncome', 'ebitda', 'operatingExpenses', 'epsDiluted'] },
    { label: 'Cash Flow', columns: ['freeCashFlow', 'operatingCashFlow', 'capitalExpenditure', 'commonDividendsPaid', 'commonStockRepurchased', 'stockBasedCompensation', 'netChangeInCash', 'cashAtEndOfPeriod', 'netCashProvidedByOperatingActivities', 'netCashProvidedByInvestingActivities', 'netCashProvidedByFinancingActivities'] },
    { label: 'Analyst', columns: ['consensus', 'strongBuy', 'buy', 'hold', 'sell', 'strongSell'] },
    { label: 'Rating', columns: ['rating', 'ratingScore', 'ratingRecommendation', 'ratingDetailsDCFScore', 'ratingDetailsROEScore', 'ratingDetailsROAScore', 'ratingDetailsDEScore', 'ratingDetailsPEScore', 'ratingDetailsPBScore'] },
    { label: 'Scores', columns: ['piotroskiScore', 'altmanZScore'] },
    { label: 'Performance', columns: ['perf5D', 'perf1M', 'perf3M', 'perf6M', 'perfYtd', 'perf1Y', 'perf3Y', 'perf5Y', 'perf10Y', 'perfMax'] },
    { label: 'Key Metrics', columns: ['enterpriseValueTTM', 'evToEBITDATTM', 'evToSalesTTM', 'evToFreeCashFlowTTM', 'evToOperatingCashFlowTTM', 'returnOnEquityTTM', 'returnOnAssetsTTM', 'returnOnInvestedCapitalTTM', 'returnOnCapitalEmployedTTM', 'currentRatioTTM', 'netDebtToEBITDATTM', 'earningsYieldTTM', 'freeCashFlowYieldTTM', 'grahamNumberTTM', 'workingCapitalTTM', 'tangibleAssetValueTTM'] },
    { label: 'Ratios & Margins', columns: ['grossProfitMarginTTM', 'operatingProfitMarginTTM', 'netProfitMarginTTM', 'ebitdaMarginTTM', 'ebitMarginTTM', 'pretaxProfitMarginTTM', 'priceToBookRatioTTM', 'priceToSalesRatioTTM', 'priceToEarningsGrowthRatioTTM', 'priceToFreeCashFlowRatioTTM', 'priceToOperatingCashFlowRatioTTM', 'priceToFairValueTTM', 'debtToEquityRatioTTM', 'debtToAssetsRatioTTM', 'quickRatioTTM', 'interestCoverageRatioTTM', 'dividendYieldTTM', 'dividendPayoutRatioTTM', 'dividendPerShareTTM', 'bookValuePerShareTTM', 'cashPerShareTTM', 'revenuePerShareTTM', 'freeCashFlowPerShareTTM', 'effectiveTaxRateTTM', 'assetTurnoverTTM'] },
];

export const DEFAULT_CUSTOM_COLUMNS = [
    'symbol', 'price', 'changesPercentage', 'change', 'marketCap', 'volume', 'exchange', 'sector', 'pe', 'eps', 'rsi',
];